import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { getColors } from "../../themes/theme";
import axios, { AxiosError } from "axios";

import LoginImg from "../../assets/auth/login.png";
import Logo from "../../assets/logo/biglogo.svg";
import LogoDark from "../../assets/logo/biglogo_dark.svg";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import GuestGuard from "../../guards/GuestGuard";
import { useSnackbar } from "notistack";

const LoginPage = () => {
  const navigate = useNavigate();
  const darkMode = localStorage.getItem("darkMode") === "true";
  const { enqueueSnackbar } = useSnackbar();
  const [openSubscribeDialog, setOpenSubscribeDialog] = useState(false);

  const { signIn } = useAuthContext();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/user/login`,
        { ...data }
      );

      await signIn(res.data.accessToken);
      enqueueSnackbar("Login Successful!", { variant: "success" });
      if (res.data.subscribed === 0)
        sessionStorage.setItem("showTrialModal", "true");
    } catch (err) {
      if (err instanceof AxiosError) {
        const status = err.response?.status;
        const msg = err.response?.data?.message || "Something went wrong.";

        if (status === 401 || status === 403) {
          if (msg !== "Your account has expired.") {
            enqueueSnackbar(msg, { variant: "error" });
          } else {
            setOpenSubscribeDialog(true);
          }
        } else {
          enqueueSnackbar("Server error. Please try again later.", {
            variant: "error",
          });
        }
      } else {
        enqueueSnackbar("Unexpected error. Please try again.", {
          variant: "error",
        });
      }
    }
  };

  return (
    <GuestGuard>
      <Box
        sx={{
          display: "flex",
          height: "100vh",
        }}
      >
        <Box
          sx={{
            flex: { xs: 6, sm: 6, md: 5, lg: 6 },
            display: { xs: "none", sm: "none", md: "flex" },
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <img
            src={LoginImg}
            alt="Login Background"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </Box>

        <Box
          sx={{
            flex: { xs: 4, sm: 4, md: 5, lg: 4 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: darkMode ? "#1D1E21" : "#FFFFFF",
            color: darkMode ? "#fff" : "#000",
            px: 1,
          }}
        >
          <Box
            sx={{
              width: "100%",
              textAlign: "center",
              p: 2,
            }}
          >
            <Box sx={{ mb: "50px" }}>
              <img
                src={darkMode ? LogoDark : Logo}
                alt="3D Practice Logo"
                style={{ width: "90%", maxWidth: 640 }}
              />
            </Box>

            <form onSubmit={handleSubmit(onSubmit)}>
              <TextField
                fullWidth
                label="User Name"
                variant="filled"
                placeholder="Enter your Username"
                sx={{ textAlign: "left", mb: "10px", height: "132px" }}
                {...register("username", {
                  required: "Username is required",
                })}
                error={!!errors.username}
                helperText={errors.username?.message}
              />

              <TextField
                fullWidth
                label="Password"
                type="password"
                variant="filled"
                placeholder="Enter your Password"
                sx={{ textAlign: "left", height: "132px" }}
                {...register("password", {
                  required: "Password is required",
                })}
                error={!!errors.password}
                helperText={errors.password?.message}
              />

              <Box display="flex" justifyContent="flex-end" mb={2}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "18px",
                    fontWeight: 500,
                    cursor: "pointer",
                    color: darkMode ? "#BEBEBE" : "#636363",
                    "&:hover": { color: "#2196F3" },
                  }}
                  onClick={() => navigate("/forgot_pass")}
                >
                  Forgot Password?
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "18px",
                    fontWeight: 500,
                    cursor: "pointer",
                    color: darkMode ? "#BEBEBE" : "#636363",
                    ml: 3,
                    "&:hover": { color: "#2196F3" },
                  }}
                  onClick={() => navigate("/reset_pass")}
                >
                  Reset Password
                </Typography>
              </Box>

              <Box
                display="flex"
                gap={2}
                flexDirection={{ xs: "column", sm: "row" }}
              >
                <Button
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 2,
                    py: "12px",
                    fontSize: { xs: "20px", sm: "24px" },
                    fontWeight: 500,
                    fontFamily: "Poppins",
                  }}
                  type="submit"
                >
                  Login
                </Button>

                {/* <Button
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 2,
                    py: "12px",
                    fontSize: { xs: "20px", sm: "24px" },
                    fontWeight: 500,
                    fontFamily: "Poppins",
                  }}
                  onClick={() => navigate("/signup")}
                >
                  Sign Up
                </Button> */}
              </Box>
            </form>
          </Box>
        </Box>
      </Box>
      <Dialog
        open={openSubscribeDialog}
        maxWidth="xs"
        fullWidth
        onClose={() => setOpenSubscribeDialog(false)}
      >
        <DialogTitle
          sx={{
            fontSize: { xs: "24px", sm: "32px" },
            fontWeight: 600,
            color: darkMode
              ? getColors.drawerTextDark
              : getColors.thumbnailTextLight,
          }}
        >
          Your trial has ended
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: { xs: 2, sm: 4 } }}>
            <Typography
              sx={{
                fontSize: { xs: "16px", sm: "20px" },
                fontWeight: 600,
                color: darkMode
                  ? getColors.drawerTextDark
                  : getColors.thumbnailTextLight,
              }}
            >
              Subscribe to keep enjoying full access.
            </Typography>
          </Box>

          <Box sx={{ gap: { xs: 1, sm: 2 }, margin: 0, padding: 0 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                window.open(
                  "https://buy.stripe.com/3cI28q37JdhV6Ungdu2Nq0G",
                  "_blank"
                );
              }}
              sx={{
                fontSize: { xs: "14px", sm: "18px" },
                fontWeight: 500,
                fontFamily: "Poppins",
                px: 4,
                py: 1,
                borderRadius: "5px",
                mb: 2,
              }}
            >
              Monthly Subscription
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                window.open(
                  "https://buy.stripe.com/bJe6oG7nZ2DhfqTbXe2Nq0H",
                  "_blank"
                );
              }}
              sx={{
                fontSize: { xs: "14px", sm: "18px" },
                fontWeight: 500,
                fontFamily: "Poppins",
                px: 4,
                py: 1,
                borderRadius: "5px",
              }}
            >
              Yearly Subscription
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </GuestGuard>
  );
};

export default LoginPage;
